import { useRef, useState, type FormEvent } from 'react'

import { AuthApiError, type StartEmailCodeResult } from '../../services/account/authApi'
import { getAuthErrorMessage } from '../../services/account/authErrors'
import { errorClass, inputClass, linkButtonClass, primaryButtonClass } from './styles'
import { useCountdown } from './useCountdown'

export const CODE_LENGTH = 6
const DEFAULT_RESEND_SECONDS = 60
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface EmailCodeFormProps {
  onRequestCode: (email: string) => Promise<StartEmailCodeResult>
  onVerifyCode: (email: string, code: string) => Promise<void>
}

// Passwordless email sign-in in two steps: enter an email, then enter the
// 6-digit code we send. On success Firebase's auth listener signs the player in
// and the Account page swaps to the signed-in view, so there is nothing to navigate.
export function EmailCodeForm({ onRequestCode, onVerifyCode }: EmailCodeFormProps) {
  const [email, setEmail] = useState('')
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { remaining, start: startCountdown } = useCountdown()
  // State is stale within a single tick (autofill plus a click can both fire before
  // a re-render), so the real double-submit guard is this ref; `submitting` only
  // drives the UI.
  const inFlight = useRef(false)

  const sendCode = async (address: string) => {
    if (inFlight.current) return
    if (!EMAIL_PATTERN.test(address.trim())) {
      setError(getAuthErrorMessage(new AuthApiError({ code: 'AUTH_INVALID_REQUEST' })))
      return
    }

    inFlight.current = true
    setError(null)
    setSubmitting(true)
    try {
      const result = await onRequestCode(address)
      setCodeSentTo(address.trim())
      setCode('')
      startCountdown(result.resendAfterSec || DEFAULT_RESEND_SECONDS)
    } catch (err) {
      setError(getAuthErrorMessage(err))
      // Honour the server's cool-down so the resend button doesn't invite a
      // request that is certain to be refused.
      if (err instanceof AuthApiError && err.code === 'AUTH_RATE_LIMITED' && err.retryAfterSec) {
        startCountdown(err.retryAfterSec)
      }
    } finally {
      inFlight.current = false
      setSubmitting(false)
    }
  }

  const verify = async (digits: string) => {
    if (inFlight.current || codeSentTo === null || digits.length !== CODE_LENGTH) return

    inFlight.current = true
    setError(null)
    setSubmitting(true)
    try {
      await onVerifyCode(codeSentTo, digits)
    } catch (err) {
      setError(getAuthErrorMessage(err))
      setCode('')
    } finally {
      inFlight.current = false
      setSubmitting(false)
    }
  }

  const handleCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, CODE_LENGTH)
    setCode(digits)
    // Submit as soon as the last digit lands (typed, pasted or autofilled).
    if (digits.length === CODE_LENGTH) void verify(digits)
  }

  const changeEmail = () => {
    setCodeSentTo(null)
    setCode('')
    setError(null)
  }

  const errorView = error ? (
    <p role="alert" data-testid="auth-error" className={errorClass}>
      {error}
    </p>
  ) : null

  if (codeSentTo === null) {
    const onSubmitEmail = (event: FormEvent) => {
      event.preventDefault()
      void sendCode(email)
    }

    return (
      <form onSubmit={onSubmitEmail} noValidate className="space-y-3">
        <label className="block">
          <span className="sr-only">Email address</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </label>
        {errorView}
        <button type="submit" disabled={submitting} className={primaryButtonClass}>
          {submitting ? 'Sending...' : 'Email me a code'}
        </button>
      </form>
    )
  }

  const canResend = remaining === 0 && !submitting

  const onSubmitCode = (event: FormEvent) => {
    event.preventDefault()
    void verify(code)
  }

  return (
    <form onSubmit={onSubmitCode} className="space-y-3">
      <p className="text-sm text-white/60">
        We sent a 6-digit code to {codeSentTo}. It expires in 10 minutes.
      </p>
      <label className="block">
        <span className="sr-only">6-digit code</span>
        <input
          type="text"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          // No maxLength: browsers would cut a pasted "123 456" off at six characters,
          // before the spaces are stripped. handleCodeChange keeps the first six digits.
          placeholder="123456"
          value={code}
          disabled={submitting}
          onChange={(e) => handleCodeChange(e.target.value)}
          className={`${inputClass} text-center text-2xl tracking-[0.5em]`}
        />
      </label>
      {errorView}
      <button type="submit" disabled={submitting || code.length !== CODE_LENGTH} className={primaryButtonClass}>
        {submitting ? 'Signing in...' : 'Sign in'}
      </button>
      <div className="flex items-center justify-between">
        <button type="button" disabled={!canResend} onClick={() => void sendCode(codeSentTo)} className={linkButtonClass}>
          {remaining > 0 ? `Resend code in ${remaining}s` : 'Resend code'}
        </button>
        <button type="button" disabled={submitting} onClick={changeEmail} className={linkButtonClass}>
          Use a different email
        </button>
      </div>
    </form>
  )
}
